import { Injectable, TemplateRef } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type InfoType = 'workspace' | 'session' | 'workspace-definition';

/**
 * Active header projection for an info card (session / workspace / definition).
 *
 * **Pin preference** (`isPinned` / localStorage) means *layout*: when true, the live info
 * component registers its template here so the shell shows it **under the main toolbar**
 * instead of in scrolling page content. It does **not** mean "show this card on every route":
 * only routes that mount a matching info component should register; when navigating away,
 * registration must clear so the shell does not keep a dead `TemplateRef`.
 */
export interface InfoPinnedContent {
  type: InfoType;
  template: TemplateRef<unknown>;
  /** Passed to *ngTemplateOutlet `context` (e.g. `let-workspace="workspace"`). */
  context: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root',
})
export class InfoPinService {
  private readonly STORAGE_PREFIX = 'info-pin-';
  private pinStates = new Map<InfoType, BehaviorSubject<boolean>>();
  private pinnedContent$ = new BehaviorSubject<InfoPinnedContent | null>(null);

  constructor() {
    // Initialize pin states from localStorage
    const types: InfoType[] = ['workspace', 'session', 'workspace-definition'];
    types.forEach((type) => {
      const key = this.getStorageKey(type);
      const stored = localStorage.getItem(key);
      const isPinned = stored === 'true';
      this.pinStates.set(type, new BehaviorSubject<boolean>(isPinned));
    });
  }

  /**
   * Get the current pin state for a given info type
   */
  isPinned(type: InfoType): boolean {
    const state$ = this.pinStates.get(type);
    return state$ ? state$.value : false;
  }

  /**
   * Get an observable of the pin state for a given info type
   */
  pinnedState$(type: InfoType): Observable<boolean> {
    const state$ = this.pinStates.get(type);
    if (!state$) {
      const newState$ = new BehaviorSubject<boolean>(false);
      this.pinStates.set(type, newState$);
      return newState$;
    }
    return state$.asObservable();
  }

  /**
   * Get observable of pinned content template
   */
  getPinnedContent$(): Observable<InfoPinnedContent | null> {
    return this.pinnedContent$.asObservable();
  }

  /** Current header projection (template + context), or null if none. */
  getPinnedContent(): InfoPinnedContent | null {
    return this.pinnedContent$.value;
  }

  /**
   * Register or clear the **active** header template for `type`.
   * Clearing does not change the user’s pin *preference* (`setPinned` / toggle).
   */
  setPinnedContent(
    type: InfoType,
    template: TemplateRef<unknown> | null,
    context?: Record<string, unknown> | null,
  ): void {
    if (template) {
      // Always clear any existing pinned content first (even if same type, to avoid stale templates)
      this.pinnedContent$.next(null);
      // Then set the new pinned content
      this.pinnedContent$.next({ type, template, context: context ?? {} });
    } else {
      // Clear if this type is currently pinned
      const current = this.pinnedContent$.value;
      if (current?.type === type) {
        this.pinnedContent$.next(null);
      }
    }
  }

  /**
   * Persist whether this info type prefers **header** layout (true) vs **in-page** (false).
   * Does not by itself register a template; the live info component does that when mounted.
   */
  setPinned(type: InfoType, pinned: boolean): void {
    const state$ = this.pinStates.get(type);
    if (state$) {
      state$.next(pinned);
      const key = this.getStorageKey(type);
      localStorage.setItem(key, String(pinned));
    } else {
      const newState$ = new BehaviorSubject<boolean>(pinned);
      this.pinStates.set(type, newState$);
      const key = this.getStorageKey(type);
      localStorage.setItem(key, String(pinned));
    }

    // Clear pinned content if unpinning
    if (!pinned) {
      const current = this.pinnedContent$.value;
      if (current?.type === type) {
        this.pinnedContent$.next(null);
      }
    }
  }

  /**
   * Toggle the pin state for a given info type
   */
  togglePinned(type: InfoType): void {
    const current = this.isPinned(type);
    this.setPinned(type, !current);
  }

  private getStorageKey(type: InfoType): string {
    return `${this.STORAGE_PREFIX}${type}`;
  }
}
